import SplitPanel from "@/pages/shared/SplitPanel";
import { pickClass, setActiveClass } from "@/lib/activeClass";
import { useEffect, useMemo, useState } from "react";
import { api, formatError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Plus, Search, Download, Upload, MessageCircle, Trash2, Edit3, RefreshCw,
} from "lucide-react";

const EMPTY = {
  prenom: "", nom: "", sexe: "M", date_naissance: "", lieu_naissance: "",
  telephone_parent: "", tuteur: "", adresse: "", matricule: "",
};

export default function Students() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);

  const loadClasses = async () => {
    try {
      const { data } = await api.get("/classes");
      setClasses(data);
      if (!classId && data.length > 0) setClassId(pickClass(data));
    } catch (e) { toast.error(formatError(e)); }
  };
  const loadStudents = async () => {
    if (!classId) { setStudents([]); return; }
    try {
      const { data } = await api.get(`/students`, { params: { class_id: classId } });
      setStudents(data);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { loadClasses(); }, []);
  useEffect(() => { loadStudents(); }, [classId]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const base = s
      ? students.filter((st) => `${st.prenom} ${st.nom} ${st.matricule || ""}`.toLowerCase().includes(s))
      : students;
    return [...base].sort(
      (a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" }) ||
        (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" })
    );
  }, [students, search]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (st) => { setEditing(st); setForm({ ...EMPTY, ...st }); setOpen(true); };

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/students/${editing.id}`, form);
        toast.success("Élève modifié");
      } else {
        const cls = classes.find((c) => c.id === classId);
        await api.post("/students", { ...form, class_id: classId, school_id: cls?.school_id });
        toast.success("Élève ajouté");
      }
      setOpen(false);
      loadStudents();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (st) => {
    if (!confirm(`Supprimer ${st.prenom} ${st.nom} ?`)) return;
    try {
      await api.delete(`/students/${st.id}`);
      toast.success("Supprimé");
      loadStudents();
    } catch (e) { toast.error(formatError(e)); }
  };

  const doImport = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/students/import?class_id=${classId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.created} élève(s) importé(s)`);
      setImportOpen(false); setFile(null);
      loadStudents();
    } catch (e) { toast.error(formatError(e)); }
  };

  const exportTo = (format) => {
    const url = `${API_BASE}/students/export?class_id=${classId}&format=${format}`;
    window.open(url, "_blank");
  };

  const regenerateMatricules = async () => {
    if (!classId) return;
    if (!window.confirm("Régénérer tous les matricules de la classe (ordre alphabétique) ?")) return;
    try {
      const { data } = await api.post(`/students/regenerate-matricules?class_id=${classId}`);
      toast.success(`${data.updated}/${data.total} matricules régénérés`);
      loadStudents();
    } catch (e) { toast.error(formatError(e)); }
  };

  const waLink = (st) => {
    if (!st.telephone_parent) return null;
    const tel = String(st.telephone_parent).replace(/\D/g, "");
    const msg = encodeURIComponent(`Bonjour, concernant ${st.prenom} ${st.nom}…`);
    return `https://wa.me/${tel.startsWith("221") ? tel : "221" + tel}?text=${msg}`;
  };

  const canEdit = user.role === "maitre";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Élèves</h1>
          <p className="text-sm text-[#5C736A]">{filtered.length} élève(s) affiché(s)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={classId} onValueChange={(v) => { setClassId(v); setActiveClass(v); }}>
            <SelectTrigger className="w-56" data-testid="select-class"><SelectValue placeholder="Classe" /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom} : {c.effectif ?? 0} élève(s)</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => exportTo("xlsx")} data-testid="export-xlsx-btn"><Download className="w-4 h-4 mr-1" /> Excel</Button>
          <Button variant="outline" onClick={() => exportTo("pdf")} data-testid="export-pdf-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          {canEdit && (
            <>
              <Button variant="outline" onClick={regenerateMatricules} data-testid="regen-matricules-btn" title="Régénérer les matricules dans l'ordre alphabétique">
                <RefreshCw className="w-4 h-4 mr-1" /> Matricules
              </Button>
              <Dialog open={importOpen} onOpenChange={setImportOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="import-btn"><Upload className="w-4 h-4 mr-1" /> Importer</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Importer des élèves</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <p className="text-sm text-[#5C736A]">
                      Fichier Excel (.xlsx) ou CSV avec colonnes : <code>prenom, nom, sexe, date_naissance, lieu_naissance, telephone_parent, tuteur, adresse, matricule</code>.
                    </p>
                    <Input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="import-file" />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setImportOpen(false)}>Annuler</Button>
                    <Button onClick={doImport} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="import-submit">Importer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" onClick={openCreate} data-testid="add-student-btn">
                <Plus className="w-4 h-4 mr-1" /> Nouvel élève
              </Button>
            </>
          )}
        </div>
      </div>

      {canEdit && <SplitPanel cls={classes.find((c) => c.id === classId)} classes={classes} students={students} />}

      <div className="ek-card p-0">
        <div className="p-3 border-b border-[#E2E8F0] flex items-center gap-2">
          <Search className="w-4 h-4 text-[#5C736A]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, prénom, matricule)…" className="border-0 focus-visible:ring-0" data-testid="student-search" />
        </div>
        <div className="ek-table-wrap">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">N°</TableHead>
                <TableHead>Matricule</TableHead>
                <TableHead>Prénom</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Sexe</TableHead>
                <TableHead>Date naissance</TableHead>
                <TableHead>Lieu naissance</TableHead>
                <TableHead>Tuteur / Parent</TableHead>
                <TableHead>N° responsable</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((st, idx) => (
                <TableRow key={st.id} data-testid={`student-row-${st.matricule}`}>
                  <TableCell className="text-center font-bold text-[#5C736A]">{idx + 1}</TableCell>
                  <TableCell className="font-mono text-xs">{st.matricule}</TableCell>
                  <TableCell>{st.prenom}</TableCell>
                  <TableCell className="font-semibold">{st.nom}</TableCell>
                  <TableCell>{st.sexe === "F" ? "Féminin" : "Masculin"}</TableCell>
                  <TableCell>{st.date_naissance || "—"}</TableCell>
                  <TableCell>{st.lieu_naissance || "—"}</TableCell>
                  <TableCell>{st.tuteur || "—"}</TableCell>
                  <TableCell>{st.telephone_parent || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {waLink(st) && (
                        <a href={waLink(st)} target="_blank" rel="noreferrer">
                          <Button size="icon" variant="ghost" title="WhatsApp parent"><MessageCircle className="w-4 h-4 text-green-600" /></Button>
                        </a>
                      )}
                      {canEdit && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(st)}><Edit3 className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => del(st)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-sm text-[#5C736A] py-10">Aucun élève. Ajoutez-en un !</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* dialog create/edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Modifier l'élève" : "Nouvel élève"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Prénom *</Label><Input data-testid="st-prenom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} /></div>
            <div><Label>Nom *</Label><Input data-testid="st-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
            <div>
              <Label>Sexe</Label>
              <Select value={form.sexe} onValueChange={(v) => setForm({ ...form, sexe: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Masculin</SelectItem>
                  <SelectItem value="F">Féminin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date naissance</Label><Input type="date" value={form.date_naissance || ""} onChange={(e) => setForm({ ...form, date_naissance: e.target.value })} /></div>
            <div><Label>Lieu naissance</Label><Input value={form.lieu_naissance || ""} onChange={(e) => setForm({ ...form, lieu_naissance: e.target.value })} /></div>
            <div><Label>Téléphone parent</Label><Input value={form.telephone_parent || ""} onChange={(e) => setForm({ ...form, telephone_parent: e.target.value })} placeholder="ex 7XX XX XX" /></div>
            <div><Label>Tuteur</Label><Input value={form.tuteur || ""} onChange={(e) => setForm({ ...form, tuteur: e.target.value })} /></div>
            <div><Label>Matricule</Label><Input value={form.matricule || ""} onChange={(e) => setForm({ ...form, matricule: e.target.value })} placeholder="Auto si vide" /></div>
            <div className="sm:col-span-2"><Label>Adresse</Label><Input value={form.adresse || ""} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="student-submit">{editing ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
